"use client";

import { Section } from "@/components/section";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { hasData } from "@/helpers/has-data";
import { getSystemIcon } from "@/lib/system-lucide-icon-map";
import { Icon } from "@/components/common/icon";
import Image from "next/image";
import Link from "next/link";
import { handleScroll } from "@/helpers/scroll-handler";

type OneToOnePrivateTrainingProps = {
  section: PageSections;
  data: {
    buttonAction: string;
    buttonText: string;
    description: string;
    heading: string;
    seoHeading: string;
    imagePath: string;
    imageAltText: string;
    cards: {
      icon: string;
      title: string;
    }[];
  };
};

export default function OneToOnePrivateTraining({ data, section }: OneToOnePrivateTrainingProps) {
  if (!hasData(data)) return null;

  return (
    <Section
      id={section.sectionId}
      className={section.sectionClass}
      title={data.heading}
      seoTitle={data.seoHeading}
      paragraph={data.description}
    >
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 sm:col-span-4">
          <AspectRatio ratio={1 / 1}>
            <Image
              src={data.imagePath}
              priority
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              alt={data.imageAltText}
              className="mx-auto object-contain"
            />
          </AspectRatio>
        </div>
        <div className="col-span-12 grid grid-cols-1 gap-5 sm:col-span-8 sm:grid-cols-3">
          {data.cards.map((card, index) => (
            <Card className="h-full" key={`one-to-one-training-process-${index}`}>
              <CardHeader className="text-primary">
                <Icon name={getSystemIcon(card.icon)} className="text-primary" />
              </CardHeader>
              <CardContent>
                <Text>{card.title}</Text>
              </CardContent>
            </Card>
          ))}

          <Box className="mt-5">
            <Button
              nativeButton={false}
              render={<Link href={data?.buttonAction} onClick={handleScroll} />}
            >
              {data.buttonText}
            </Button>
          </Box>
        </div>
      </div>
    </Section>
  );
}
